import IndexedDBHelper from '@/utils/indexDB';
import { BaseImageStatus } from '../layoutContext/ImageProcessingContext';

/**
 * 图像缓存管理器 - 为每个业务提供独立的缓存空间
 */
class ImageCacheManager {
  private static instances = new Map<string, ImageCacheManager>();
  private dbHelper: IndexedDBHelper;

  private constructor(businessName: string) {
    this.dbHelper = new IndexedDBHelper(
      `imageProcessing_${businessName}`,
      'imageCache',
      1
    );
  }

  static getInstance(businessName: string): ImageCacheManager {
    if (!this.instances.has(businessName)) {
      this.instances.set(businessName, new ImageCacheManager(businessName));
    }
    return this.instances.get(businessName)!;
  }

  async saveImage<T extends BaseImageStatus>(
    id: string,
    imageStatus: T
  ): Promise<void> {
    try {
      await this.dbHelper.storeData(id, imageStatus);
    } catch (error) {
      console.error(`[${this.dbHelper['dbName']}] 保存图像失败:`, error);
    }
  }

  async getImage<T extends BaseImageStatus>(id: string): Promise<T | null> {
    try {
      return await this.dbHelper.getData<T>(id);
    } catch (error) {
      console.error(`[${this.dbHelper['dbName']}] 获取图像失败:`, error);
      return null;
    }
  }

  async getAllImages<T extends BaseImageStatus>(): Promise<Map<string, T>> {
    try {
      const db = await this.dbHelper.openDatabase();
      const transaction = db.transaction('imageCache', 'readonly');
      const store = transaction.objectStore('imageCache');

      return new Promise((resolve) => {
        const request = store.getAll();
        request.onsuccess = () => {
          const images = new Map<string, T>();
          request.result.forEach((item: { id: string; data: T }) => {
            images.set(item.id, item.data);
          });
          resolve(images);
        };
      });
    } catch (error) {
      console.error(`[${this.dbHelper['dbName']}] 获取所有图像失败:`, error);
      return new Map();
    }
  }

  async removeImage(id: string): Promise<void> {
    try {
      await this.dbHelper.deleteData(id);
    } catch (error) {
      console.error(`[${this.dbHelper['dbName']}] 删除图像失败:`, error);
    }
  }

  async clearImages(): Promise<void> {
    try {
      await this.dbHelper.clearAllData();
    } catch (error) {
      console.error(`[${this.dbHelper['dbName']}] 清空图像失败:`, error);
    }
  }
}

/**
 * 创建业务专用的图像缓存实例
 * @param businessName 业务名称，用于区分不同业务的缓存
 */
export const createImageIndexDB = (businessName: string) => {
  const manager = ImageCacheManager.getInstance(businessName);
  return {
    saveImage: manager.saveImage.bind(manager),
    getImage: manager.getImage.bind(manager),
    getAllImages: manager.getAllImages.bind(manager),
    removeImage: manager.removeImage.bind(manager),
    clearImages: manager.clearImages.bind(manager),
  };
};

// 默认实例（向后兼容）
export const imageIndexDB = createImageIndexDB('default');
